import ReactDOM from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import { router } from "./routes";

import "./styles.css";
import reportWebVitals from "./reportWebVitals.ts";
// core styles are required for all packages
import "@mantine/core/styles.css";

// other css files are required only if
// you are using components from the corresponding package
import "@mantine/dates/styles.css";
import "@mantine/dropzone/styles.css";
import {
  Flex,
  MantineProvider,
  Title,
  Text,
  Button,
  Group,
  ScrollArea,
  Box,
  Stack,
} from "@mantine/core";
import "@mantine/carousel/styles.css";

import theme from "./theme.ts";
import { AuthProvider } from "./auth.tsx";
import React from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { NotificationProvider } from "./components/notification-context";
// import '@mantine/code-highlight/styles.css';
// ...
import "@mantine/core/styles.css";
import "@mantine/notifications/styles.css";
import { TourProvider, useTour } from "@reactour/tour";
import { Notifications } from "@mantine/notifications";
import { useUpdateMe, useMe } from "./api/index.ts";

const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false } },
});

function InnerApp() {
  return <RouterProvider router={router} />;
}

function TourWrapper() {
  const { mutateAsync: updateUserProfile } = useUpdateMe();
  const { data: userData, isLoading } = useMe();
  const { setIsOpen } = useTour();

  React.useEffect(() => {
    setIsOpen(false);
    
    if (!isLoading && userData) {
      const skipIntro = Boolean(userData.skip_intro);
      const hasSeenWelcomeTour = Boolean((userData as any).has_seen_welcome_tour);
      
      if (skipIntro || hasSeenWelcomeTour) {
        setIsOpen(false);
      }
    }
  }, [isLoading, userData, setIsOpen]);

  const steps = [
    {
      selector: "[data-tour='welcome']",
      content: (
        <Flex direction="column" gap="md">
          <Title order={5}>Your health journey begins here</Title>
          <Text>
            We're here to guide you—step by step—with personalized support,
            smart insights, and tools that help you take control of your
            well-being.
          </Text>
        </Flex>
      ),
      position: "center",
    },
    {
      selector: "[data-tour='health-journey']",
      content: (
        <Flex direction="column" gap="md">
          <Title order={5}>Your Health Journey at a Glance</Title>
          <Text>
            Here's a snapshot of your progress since joining OneSelf Path.
            You've taken meaningful steps toward better health by engaging with
            care OneSelf Path®, completing waypoints, and staying active in your
            journey. Keep up the great work—you're 68% of the way there!
          </Text>
        </Flex>
      ),
      position: "center",
    },
    {
      selector: "[data-tour='check-health-data']",
      content: (
        <Flex direction="column" gap="md">
          <Title order={5}>Check My Health Data</Title>
          <Text>
            This feature allows you to connect your health records to the app
            and make sure your information is up to date. When you tap on{" "}
            <Text span fw={600} c="blue">
              'Check My Health Data,'
            </Text>{" "}
            the app will:
          </Text>
          <Text component="ul" style={{ marginLeft: "20px", marginTop: "8px" }}>
            <Text component="li" size="sm" mb="xs">
              Look for the latest updates in your medical records (like new test
              results, medications, or diagnoses).
            </Text>
            <Text component="li" size="sm" mb="xs">
              Update your profile with the most current health information.
            </Text>
            <Text component="li" size="sm">
              Provide new educational tips and insights based on your latest
              health data.
            </Text>
          </Text>
        </Flex>
      ),
      position: "center",
    },
    {
      selector: "[data-tour='path-features']",
      content: () => (
        <Box style={{ width: "100%", maxWidth: "400px" }}>
          <Group justify="center" gap="xs" mb="md">
            <Button size="xs" variant="transparent">
              New
            </Button>
            <Button size="xs">Inprogress</Button>{" "}
            <Button size="xs" variant="transparent">
              Completed
            </Button>
          </Group>

          <ScrollArea h={280} type="auto" scrollbarSize={6}>
            <Stack gap="md">
              <Box>
                <Text size="sm" fw={600} mb="xs">
                  New (Not Started)
                </Text>
                <Text
                  component="ul"
                  style={{ marginLeft: "20px", marginTop: "4px" }}
                >
                  <Text component="li" size="sm" mb="xs">
                    These are new care opportunities identified through your
                    most recent health data analysis.
                  </Text>
                  <Text component="li" size="sm" mb="xs">
                    It also includes paths you've seen before but haven't
                    started yet.
                  </Text>
                  <Text component="li" size="sm">
                    These are listed as "New" to help you take the first step
                    when you're ready.
                  </Text>
                </Text>
              </Box>

              {/* In Progress Section - keeping original text and styling */}
              <Box>
                <Text size="sm" fw={600} mb="xs">
                  In Progress
                </Text>
                <Text
                  component="ul"
                  style={{ marginLeft: "20px", marginTop: "4px" }}
                >
                  <Text component="li" size="sm" mb="xs">
                    These are the care paths you've already started but haven't
                    completed yet.
                  </Text>
                  <Text component="li" size="sm" mb="xs">
                    You'll see these here if there are no new paths added and
                    you still have open actions.
                  </Text>
                  <Text component="li" size="sm">
                    This section helps you pick up where you left off.
                  </Text>
                </Text>
              </Box>

              {/* Completed Section - keeping original text and styling */}
              <Box>
                <Text size="sm" fw={600} mb="xs">
                  Completed
                </Text>
                <Text
                  component="ul"
                  style={{ marginLeft: "20px", marginTop: "4px" }}
                >
                  <Text component="li" size="sm" mb="xs">
                    These are the care paths you've finished.
                  </Text>
                  <Text component="li" size="sm" mb="xs">
                    Each completed path will show:
                    <Text
                      size="sm"
                      style={{ marginLeft: "20px", marginTop: "4px" }}
                    >
                      • The title of the condition or topic (e.g., Heart Health)
                    </Text>
                    <Text
                      size="sm"
                      style={{ marginLeft: "20px", marginTop: "2px" }}
                    >
                      • A short summary of what it was about
                    </Text>
                    <Text
                      size="sm"
                      style={{ marginLeft: "20px", marginTop: "2px" }}
                    >
                      • The date you completed it
                    </Text>
                  </Text>
                  <Text component="li" size="sm">
                    These help track your progress and what you've learned over
                    time.
                  </Text>
                </Text>
              </Box>
            </Stack>
          </ScrollArea>
        </Box>
      ),
      position: "center",
    },
  ];

  return (
    <TourProvider
      steps={steps as any}
      showBadge={false}
      showDots={false}
      styles={{
        popover: (base) => ({
          ...base,
          borderRadius: 10,
          maxWidth: "450px",
          width: "90vw",
          maxHeight: "80vh",
          "@media (max-width: 768px)": {
            width: "95vw",
            maxHeight: "85vh",
          },
        }),
        dot: (base) => ({
          ...base,
          background: "#ef5a3d",
        }),
      }}
      showCloseButton={false}
      nextButton={(props) => {
        const isLastStep = props.currentStep === steps.length - 1;
        return (
          <Button
            size="xs"
            radius="8px"
            onClick={() => {
              if (isLastStep) {
                props.setIsOpen(false);
                if (userData?.id) {
                  updateUserProfile({
                    id: userData.id,
                    data: { has_seen_welcome_tour: true } as any,
                  });
                }
              } else {
                props.setCurrentStep(props.currentStep + 1);
              }
            }}
          >
            Got it
          </Button>
        );
      }}
      prevButton={(props) => (
        <Button
          variant="transparent"
          size="xs"
          radius="8px"
          onClick={() => {
            props.setIsOpen(false);
            if (userData?.id) {
              updateUserProfile({
                id: userData.id,
                data: { skip_intro: true },
              });
            }
          }}
        >
          Skip Intro
        </Button>
      )}
      disableDotsNavigation
    >
      <InnerApp />
    </TourProvider>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <NotificationProvider>
          <MantineProvider theme={theme} defaultColorScheme="light">
            <Notifications position="top-right" />
            <TourWrapper />
          </MantineProvider>
        </NotificationProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

// Render the app. Mock vs real data is decided in src/lib/data-provider.ts.
const rootElement = document.getElementById("app");
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
